import { PlusCircle } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import FloatingInput from '../FloatingInput.jsx';
import FloatingInputWithNA from '../FloatingInputWithNA.jsx';
import DatePicker from '../../Common/DatePicker.jsx';
import SearchableSelect from '../SearchableSelect.jsx';
import { Eye, Upload } from 'lucide-react';
import { employeeService,API_URL } from '../../../services/api';
import toast from 'react-hot-toast';
import {
    isSameAadhaar,
    FAMILY_AADHAAR_SAME_AS_EMPLOYEE,
} from './validationHelpers';

export function FamilyDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    isLastTab,
    readOnly = false,
    onSaveTrigger,
}) {
    // State for validation errors
    const [validationErrors, setValidationErrors] = useState({
        orphanStatus: '',
        familyMembers: []
    });

    // Ensure familyMembers is always an array and handle orphan status
    const formData = {
        ...employeeData,
        familyMembers: employeeData.familyMembers || [],
        isOrphan: employeeData.isOrphan || false
    };
    
    // Check if ESI is enabled for this employee (persisted or current flag).
    // Backend may send these flags as boolean, number (0/1), or string ("true"/"false").
    const parseBoolFlag = (value) => {
        if (value === true) return true;
        if (value === false || value == null) return false;
        if (typeof value === 'number') return value === 1;
        if (typeof value === 'string') {
            const v = value.trim().toLowerCase();
            return v === 'true' || v === '1' || v === 'yes' || v === 'y';
        }
        return false;
    };

    const isESIEnabled =
        parseBoolFlag(formData.persistedEsiApplicable) ||
        parseBoolFlag(formData.esiApplicable);

    const [familyPhotoUrlCache, setFamilyPhotoUrlCache] = useState(() => new Map());
    const [pendingFamilyPhotoFiles, setPendingFamilyPhotoFiles] = useState({});
    const [pendingFamilyPhotoFilesByIndex, setPendingFamilyPhotoFilesByIndex] = useState({});

    useEffect(() => {
        let cancelled = false;

        async function prefetchFamilyPhotoUrls() {
            try {
                if (!employeeData?.id) return;
                const members = Array.isArray(formData.familyMembers) ? formData.familyMembers : [];
                const candidates = members.filter(m => {
                    if (!m?.id) return false;
                    if (!m?.photo) return false;
                    if (typeof m.photo !== 'string') return false;
                    if (m.photo.startsWith('data:')) return false;
                    return true;
                });

                for (const m of candidates) {
                    if (cancelled) return;
                    if (familyPhotoUrlCache.has(m.id)) continue;
                    try {
                        const resp = await employeeService.getFamilyMemberPhotoUrl(employeeData.id, m.id);
                        const url = resp?.data?.url || resp?.url || null;
                        if (!url) continue;
                        if (cancelled) return;
                        setFamilyPhotoUrlCache(prev => {
                            const next = new Map(prev);
                            next.set(m.id, url);
                            return next;
                        });
                    } catch (e) {
                        // ignore; fallback handled in UI
                    }
                }
            } catch (e) {
                // ignore
            }
        }

        prefetchFamilyPhotoUrls();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeData?.id, formData.familyMembers]);
    

    
    // Load and format family data when editing - only run once when component mounts
    useEffect(() => {
        if (employeeData.familyMembers && employeeData.familyMembers.length > 0) {
            // Ensure each family member has all required fields with proper defaults
            const formattedFamilyMembers = employeeData.familyMembers.map(member => {
                // Handle both frontend and backend field names with comprehensive mapping
                const formattedMember = {
                    // Frontend fields
                    id: member.id || uuidv4(),
                    name: member.name || '',
                    dateOfBirth: member.dateOfBirth || member.date_of_birth || '',
                    relationship: member.relationship || '',
                    gender: member.gender || '',
                    phone: member.phone || member.mobileNumber || member.mobile_number || '',
                    aadhaarNumber: member.aadhaarNumber || member.aadhaar || member.aadhaar_number || '',
                    nominee: member.nominee || false,
                    sharePercentage: member.sharePercentage || member.share_percentage || 0,
                    dependent: member.dependent || false,
                    photo: member.photo || null,
                    // photoFileName: member.photoFileName || member.photo_name || member.photo_file_name || '',
                    photoChanged: false, // Track if photo has been modified
                    
                    // Backend fields for proper sync
                    user_id: member.user_id || employeeData.userId || employeeData.user?.id,
                    created_at: member.created_at,
                    updated_at: member.updated_at,
                    
                    // Additional fields that might come from backend
                    // photo_path: member.photo_path || member.photoPath || '',
                    nominee_status: member.nominee_status || member.nominee || false,
                    dependent_status: member.dependent_status || member.dependent || false,
                    share_percentage: member.share_percentage || member.sharePercentage || 0,
                    mobile_number: member.mobile_number || member.phone || '',
                    aadhaar_number: member.aadhaar_number || member.aadhaarNumber || '',
                    date_of_birth: member.date_of_birth || member.dateOfBirth || ''
                };
                
                return formattedMember;
            });
            
            setEmployeeData(prev => ({
                ...prev,
                familyMembers: formattedFamilyMembers
            }));
        }
    }, [employeeData.id]); // Only run when employee ID changes
    

    


    const handleInputChange = useCallback((e) => {
        const { name, checked } = e.target;

        // Toggle flags like isOrphan without clearing existing data so that
        // checking/unchecking does not wipe unsaved family rows.
        setEmployeeData(prev => ({
            ...prev,
            [name]: checked,
            familyMembers: prev.familyMembers,
        }));
        
        // Clear validation errors when orphan status changes
        if (name === 'isOrphan') {
            setValidationErrors(prev => ({
                ...prev,
                orphanStatus: ''
            }));
        }
    }, [setEmployeeData]);

    const handleNestedInputChange = useCallback((index, field, value) => {
        setEmployeeData(prev => ({
            ...prev,
            familyMembers: prev.familyMembers.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            ),
        }));
        
        // Clear validation errors when user enters valid data
        if (validationErrors.familyMembers && validationErrors.familyMembers[index]) {
            const memberErrors = { ...validationErrors.familyMembers[index] };
            
            // Clear specific field errors based on what was entered
            if (field === 'sharePercentage' && value && parseFloat(value) > 0 && parseFloat(value) <= 100) {
                delete memberErrors.sharePercentage;
            } else if (field === 'name' && value && value.trim() !== '') {
                delete memberErrors.name;
            } else if (field === 'dateOfBirth' && value) {
                delete memberErrors.dateOfBirth;
            } else if (field === 'relationship' && value) {
                delete memberErrors.relationship;
            } else if (field === 'gender' && value) {
                delete memberErrors.gender;
            } else if (field === 'phone' && ((value && /^[6-9]\d{9}$/.test(value)) || value === 'NA')) {
                delete memberErrors.phone;
            } else if (field === 'aadhaarNumber') {
                if (isSameAadhaar(value, employeeData.aadharNumber)) {
                    memberErrors.aadhaarNumber = FAMILY_AADHAAR_SAME_AS_EMPLOYEE;
                } else if ((value && /^[0-9]{12}$/.test(value) &&
                      value !== '000000000000' && value !== '111111111111' && value !== '999999999999') || value === 'NA' || value === 'Not Available') {
                    delete memberErrors.aadhaarNumber;
                }
            }
            
            // Update validation errors
            setValidationErrors(prev => ({
                ...prev,
                familyMembers: prev.familyMembers.map((memberError, i) =>
                    i === index ? memberErrors : memberError
                )
            }));
        }
    }, [setEmployeeData, validationErrors, employeeData.aadharNumber]);

    // Handle nominee checkbox changes specifically
    const handleNomineeChange = useCallback((index, checked) => {
        setEmployeeData(prev => ({
            ...prev,
            familyMembers: prev.familyMembers.map((item, i) =>
                i === index ? { ...item, nominee: checked } : item
            ),
        }));
        
        // Clear share percentage error when nominee is unchecked
        if (!checked && validationErrors.familyMembers && validationErrors.familyMembers[index]) {
            const memberErrors = { ...validationErrors.familyMembers[index] };
            delete memberErrors.sharePercentage;
            
            setValidationErrors(prev => ({
                ...prev,
                familyMembers: prev.familyMembers.map((memberError, i) =>
                    i === index ? memberErrors : memberError
                )
            }));
        }
    }, [setEmployeeData, validationErrors]);

    // Calculate total share percentage for nominees
    const getTotalSharePercentage = useCallback(() => {
        return formData.familyMembers
            .filter(member => member.nominee)
            .reduce((total, member) => total + (parseFloat(member.sharePercentage) || 0), 0);
    }, [formData.familyMembers]);

    // Calculate age from date of birth (returns {years, months})
    const calculateAge = useCallback((dateOfBirth) => {
        if (!dateOfBirth) return null;
        try {
            const birthDate = new Date(dateOfBirth);
            const today = new Date();
            
            let years = today.getFullYear() - birthDate.getFullYear();
            let months = today.getMonth() - birthDate.getMonth();
            
            if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
                years--;
                months += 12;
            }
            
            if (today.getDate() < birthDate.getDate()) {
                months--;
                if (months < 0) {
                    months += 12;
                    years--;
                }
            }
            
            return { years, months };
        } catch (e) {
            return null;
        }
    }, []);

    const handleAddItem = useCallback(() => {
        setEmployeeData(prev => ({
            ...prev,
            familyMembers: [
                ...(prev.familyMembers || []),
                {
                    id: uuidv4(),
                    name: '',
                    dateOfBirth: '',
                    relationship: '',
                    gender: '',
                    phone: '',
                    nominee: false,
                    sharePercentage: 0,
                    dependent: false,
                    photo: null,
                    photoFileName: '',
                },
            ],
        }));
    }, [setEmployeeData]);

    const handleRemoveItem = useCallback((index) => {
        setEmployeeData(prev => ({
            ...prev,
            familyMembers: prev.familyMembers.filter((_, i) => i !== index),
        }));
    }, [setEmployeeData]);

    const handlePhotoUpload = useCallback(async (index, memberId, file) => {
        if (!file) return;
            // Validate file type - allow only images
            const allowedTypes = [
                'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'
            ];
            
            if (!allowedTypes.includes(file.type)) {
                toast.error('Only image files (JPG, PNG, GIF, BMP, WebP) are allowed');
                return;
            }

            try {
                setPhotoUploading(prev => ({ ...prev, [index]: true }));

                // Defer backend upload until user clicks Update & Next.
                // Store pending file keyed by current member id (temp uuid or numeric).
                const memberKey = memberId;
                if (memberKey !== undefined && memberKey !== null) {
                    setPendingFamilyPhotoFiles(prev => ({
                        ...prev,
                        [String(memberKey)]: file
                    }));
                }

                setPendingFamilyPhotoFilesByIndex(prev => ({
                    ...prev,
                    [index]: file
                }));

                // Clear validation error for photo immediately when user selects a file
                setValidationErrors(prev => {
                    const fam = Array.isArray(prev.familyMembers) ? prev.familyMembers : [];
                    const nextFam = fam.map((mErr, i) => {
                        if (i !== index) return mErr;
                        const nextErr = { ...(mErr || {}) };
                        delete nextErr.photo;
                        return nextErr;
                    });
                    return { ...prev, familyMembers: nextFam };
                });

                const reader = new FileReader();
                reader.onloadend = () => {
                    setEmployeeData(prev => ({
                        ...prev,
                        familyMembers: prev.familyMembers.map((item, i) =>
                            i === index ? { 
                                ...item, 
                                photo: reader.result,
                                photoFileName: file.name,
                                photoChanged: true
                            } : item
                        ),
                    }));
                    setPhotoUploading(prev => ({ ...prev, [index]: false }));
                };
                reader.readAsDataURL(file);
            } catch (error) {
                console.error('Error uploading family member photo:', error);
                toast.error('Error uploading photo: ' + (error.message || 'Unknown error'));
            } finally {
                setPhotoUploading(prev => ({ ...prev, [index]: false }));
            }
    }, [setEmployeeData]);

    const handleRemovePhoto = useCallback((index) => {
        setEmployeeData(prev => ({
            ...prev,
            familyMembers: prev.familyMembers.map((item, i) =>
                i === index ? { 
                    ...item, 
                    photo: null,
                    photoFileName: '',
                    photoChanged: true // Mark as changed so it gets saved
                } : item
            ),
        }));
    }, [setEmployeeData]);

    const [photoModal, setPhotoModal] = useState({ 
        isOpen: false, 
        photo: null, 
        fileName: '',
        memberName: '',
        employeeId: '',
        memberId: null,  // For API calls to download
        isDataUrl: false // Track if photo is a data URL (newly uploaded)
    });
    const [photoUploading, setPhotoUploading] = useState({});

    const handleViewPhoto = useCallback(async (index) => {
        const member = formData.familyMembers[index];
        if (!member) return;

        const memberName = member.name || 'FamilyMember';
        const empId = employeeData?.id || employeeData?.employeeId || 'Unknown';
        const memberId = member.id;

        // Case 1: Newly uploaded photo (data URL)
        if (member.photo && typeof member.photo === 'string' && member.photo.startsWith('data:')) {
            setPhotoModal({ 
                isOpen: true, 
                photo: member.photo, 
                fileName: member.photoFileName,
                memberName: memberName,
                employeeId: empId,
                memberId: memberId,
                isDataUrl: true
            });
            return;
        }

        // Case 2: Existing photo stored in backend
        if (member.photo && employeeData?.id && member?.id) {
            try {
                const resp = await employeeService.getFamilyMemberPhotoUrl(employeeData.id, member.id);
                const url = resp?.data?.url || resp?.url || null;
                setPhotoModal({ 
                    isOpen: true, 
                    photo: url, 
                    fileName: member.photoFileName,
                    memberName: memberName,
                    employeeId: empId,
                    memberId: memberId,
                    isDataUrl: false
                });
            } catch (e) {
                console.error('Failed to load family photo signed URL', e);
                setPhotoModal({ 
                    isOpen: true, 
                    photo: null, 
                    fileName: member.photoFileName, 
                    hasExistingPhoto: true,
                    memberName: memberName,
                    employeeId: empId,
                    memberId: memberId,
                    isDataUrl: false
                });
            }
            return;
        }

        // Case 3: No photo available
        setPhotoModal({ 
            isOpen: true, 
            photo: null, 
            fileName: member.photoFileName, 
            hasExistingPhoto: true,
            memberName: memberName,
            employeeId: empId,
            memberId: memberId,
            isDataUrl: false
        });
    }, [formData.familyMembers, employeeData?.id, employeeData?.employeeId]);

    const closePhotoModal = useCallback(() => {
        setPhotoModal({ 
            isOpen: false, 
            photo: null, 
            fileName: '', 
            memberName: '', 
            employeeId: '',
            memberId: null,
            isDataUrl: false
        });
    }, []);

    // Helper function to generate sanitized filename
    const generateFileName = useCallback((memberName, employeeId, suffix = '', extension = 'jpg') => {
        const sanitizeName = (name) => (name || 'FamilyMember').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
        const sanitizedName = sanitizeName(memberName);
        const empId = employeeId || 'Unknown';
        return suffix ? `${sanitizedName}_${empId}_${suffix}.${extension}` : `${sanitizedName}_${empId}.${extension}`;
    }, []);

    // Helper function to convert data URL to blob
    const dataUrlToBlob = useCallback(async (dataUrl) => {
        const response = await fetch(dataUrl);
        return await response.blob();
    }, []);

    // Helper function to trigger download
    const triggerDownload = useCallback((blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, []);

    const downloadPhoto = useCallback(async () => {
        if (!photoModal.photo && !photoModal.memberId) return;
        
        try {
            let blob;
            let extension = 'jpg';
            
            // Case 1: Data URL (newly uploaded photo, not yet saved to backend)
            if (photoModal.isDataUrl && photoModal.photo) {
                const match = photoModal.photo.match(/data:image\/(\w+);/);
                extension = (match && match[1]) || 'png';
                blob = await dataUrlToBlob(photoModal.photo);
            } 
            // Case 2: External URL - use backend proxy to avoid CORS
            else if (photoModal.memberId && employeeData?.id) {
                try {
                    // Fetch through backend proxy (no CORS issues)
                    blob = await employeeService.getFamilyMemberPhotoBlob(employeeData.id, photoModal.memberId);
                    // Try to get extension from content type
                    if (blob.type) {
                        const typeMatch = blob.type.match(/image\/(\w+)/);
                        if (typeMatch) extension = typeMatch[1] === 'jpeg' ? 'jpg' : typeMatch[1];
                    }
                } catch (apiError) {
                    console.error('Backend proxy failed:', apiError);
                    toast.error('Failed to download photo. Please try again or contact support.');
                    return;
                }
            } else {
                toast.error('Photo not available for download');
                return;
            }
            
            if (!blob) {
                toast.error('Failed to download photo');
                return;
            }
            
            const fileName = generateFileName(photoModal.memberName, photoModal.employeeId, '', extension);
            triggerDownload(blob, fileName);
            toast.success('Photo downloaded successfully');
        } catch (error) {
            console.error('Error downloading photo:', error);
            toast.error('Failed to download photo: ' + (error.message || 'Unknown error'));
        }
    }, [photoModal, employeeData?.id, dataUrlToBlob, generateFileName, triggerDownload]);

    const downloadCompressedPhoto = useCallback(async () => {
        if (!photoModal.photo && !photoModal.memberId) return;
        
        const loadingToast = toast.loading('Compressing image...');
        
        try {
            let img = new Image();
            
            // Case 1: Data URL (newly uploaded photo)
            if (photoModal.isDataUrl && photoModal.photo) {
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = photoModal.photo;
                });
            } 
            // Case 2: External URL - fetch from backend proxy and convert to data URL
            else if (photoModal.memberId && employeeData?.id) {
                try {
                    // Fetch blob from backend (no CORS issues)
                    const blob = await employeeService.getFamilyMemberPhotoBlob(employeeData.id, photoModal.memberId);
                    
                    // Convert blob to data URL for image loading
                    const dataUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    
                    // Load into image element
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = dataUrl;
                    });
                } catch (apiError) {
                    console.error('Backend proxy failed:', apiError);
                    toast.dismiss(loadingToast);
                    toast.error('Failed to load image for compression. Please try again.');
                    return;
                }
            } else {
                toast.dismiss(loadingToast);
                toast.error('Photo not available for download');
                return;
            }
            
            // Get original dimensions
            let width = img.naturalWidth || img.width;
            let height = img.naturalHeight || img.height;
            
            if (!width || !height) {
                toast.dismiss(loadingToast);
                toast.error('Failed to load image dimensions');
                return;
            }
            
            const maxDimension = 1920; // Max width or height
            
            // Scale down if too large
            if (width > maxDimension || height > maxDimension) {
                const ratio = Math.min(maxDimension / width, maxDimension / height);
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
            }
            
            // Create canvas and draw image
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Compress to between 50KB and 100KB (strict)
            const MIN_SIZE = 50 * 1024;
            const MAX_SIZE = 100 * 1024;

            const toBlob = (q) =>
                new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));

            let blob = null;
            let attempts = 0;
            const maxAttempts = 8; // dimension-reduction attempts

            while (attempts < maxAttempts) {
                const maxQBlob = await toBlob(0.95);
                if (!maxQBlob) break;
                if (maxQBlob.size < MIN_SIZE) {
                    toast.dismiss(loadingToast);
                    toast.error('Cannot compress into 50–100KB (image is already too small).', { position: 'top-right' });
                    return;
                }
                if (maxQBlob.size <= MAX_SIZE) {
                    blob = maxQBlob;
                    break;
                }

                let lo = 0.1;
                let hi = 0.95;
                let best = null;
                for (let i = 0; i < 10; i++) {
                    const mid = (lo + hi) / 2;
                    const b = await toBlob(mid);
                    if (!b) break;
                    if (b.size > MAX_SIZE) {
                        hi = mid;
                    } else if (b.size < MIN_SIZE) {
                        lo = mid;
                    } else {
                        best = b;
                        break;
                    }
                }

                if (best) {
                    blob = best;
                    break;
                }

                width = Math.floor(width * 0.9);
                height = Math.floor(height * 0.9);
                if (width < 50 || height < 50) break;
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                attempts++;
            }
            
            if (!blob) {
                toast.dismiss(loadingToast);
                toast.error('Failed to compress image into 50–100KB.');
                return;
            }
            
            const fileName = generateFileName(photoModal.memberName, photoModal.employeeId, 'compressed', 'jpg');
            triggerDownload(blob, fileName);
            
            toast.dismiss(loadingToast);
            toast.success(`Image compressed to ${(blob.size / 1024).toFixed(2)}KB and downloaded`);
        } catch (error) {
            console.error('Error compressing and downloading photo:', error);
            toast.dismiss(loadingToast);
            toast.error('Failed to compress and download photo: ' + (error.message || 'Unknown error'));
        }
    }, [photoModal, employeeData?.id, generateFileName, triggerDownload]);

    // Validation function to check if family member data is complete
    const validateFamilyMember = (member) => {
        const errors = {};
        
        // Basic field validations
        if (!member.name || member.name.trim() === '') {
            errors.name = 'Name is required';
        }
        if (!member.dateOfBirth) {
            errors.dateOfBirth = 'Date of birth is required';
        }
        if (!member.relationship) {
            errors.relationship = 'Relationship is required';
        }
        if (!member.gender) {
            errors.gender = 'Gender is required';
        }
        
        // Phone validation - mandatory for all family members
        if (!member.phone || member.phone.trim() === '') {
            errors.phone = 'Please enter mobile number or select Not Available';
        } else if (member.phone !== 'Not Available' && !/^[6-9]\d{9}$/.test(member.phone)) {
            errors.phone = 'Mobile number must be 10 digits starting with 6, 7, 8, or 9';
        }
        
        // Aadhaar validation - mandatory for all family members
        if (!member.aadhaarNumber || member.aadhaarNumber.trim() === '') {
            errors.aadhaarNumber = 'Please enter Aadhaar number or select Not Available';
        } else if (member.aadhaarNumber !== 'Not Available' && !/^[0-9]{12}$/.test(member.aadhaarNumber)) {
            errors.aadhaarNumber = 'Aadhaar number must be 12 digits';
        } else if (member.aadhaarNumber !== 'Not Available' && (member.aadhaarNumber === '000000000000' || member.aadhaarNumber === '111111111111' || member.aadhaarNumber === '999999999999')) {
            errors.aadhaarNumber = 'Please enter a valid Aadhaar number';
        } else if (isSameAadhaar(member.aadhaarNumber, employeeData.aadharNumber)) {
            errors.aadhaarNumber = FAMILY_AADHAAR_SAME_AS_EMPLOYEE;
        }
        
        // Nominee validation
        if (member.nominee) {
            if (!member.sharePercentage || member.sharePercentage === '') {
                errors.sharePercentage = 'Please enter share percentage for nominee';
            } else if (parseFloat(member.sharePercentage) <= 0) {
                errors.sharePercentage = 'Share percentage must be greater than 0%';
            } else if (parseFloat(member.sharePercentage) > 100) {
                errors.sharePercentage = 'Share percentage cannot exceed 100%';
            }
        }
        
        // Check total share percentage for all nominees
        const nominees = formData.familyMembers.filter(m => m.nominee && m.id !== member.id);
        const totalOtherShares = nominees.reduce((total, m) => total + (parseFloat(m.sharePercentage) || 0), 0);
        const currentShare = parseFloat(member.sharePercentage) || 0;
        
        if (member.nominee && (totalOtherShares + currentShare) > 100) {
            errors.sharePercentage = `Total share percentage cannot exceed 100%. Current total: ${(totalOtherShares + currentShare).toFixed(2)}%`;
        }
        
        // Photo validation - mandatory for all family members when ESI is enabled
        if (isESIEnabled && !member.photo && !member.photoFileName) {
            errors.photo = 'Photo is mandatory for ESI filing';
        }
        
        // Log validation for debugging
        if (Object.keys(errors).length > 0) {
            console.log(`Validation errors for member ${member.name}:`, errors);
        }
        
        return errors;
    };

    // Validation function to check overall form validity
    const validateForm = () => {
        const errors = {
            orphanStatus: '',
            familyMembers: []
        };

        // If not orphan, must have at least one family member with complete information
        if (!formData.isOrphan) {
            if (!formData.familyMembers || formData.familyMembers.length === 0) {
                errors.orphanStatus = 'Please either mark as orphan or add at least one family member';
            } else {
                // Validate each family member
                const memberErrors = formData.familyMembers.map(member => validateFamilyMember(member));
                errors.familyMembers = memberErrors;
                
                // Check if at least one family member has complete information
                const hasValidMember = memberErrors.some(memberError => 
                    Object.keys(memberError).length === 0
                );
                
                // Check if the only errors are share percentage errors
                // This prevents showing the general error message when only share percentage needs fixing
                const hasOnlySharePercentageErrors = memberErrors.every(memberError => {
                    const errorKeys = Object.keys(memberError);
                    // If no errors, it's valid
                    if (errorKeys.length === 0) return true;
                    // If only share percentage error, it's considered "only share percentage errors"
                    if (errorKeys.length === 1 && errorKeys[0] === 'sharePercentage') return true;
                    // If other errors exist, it's not "only share percentage errors"
                    return false;
                });
                
                // Only show orphan status error if there are other validation issues besides share percentage
                // Share percentage errors are already displayed beside the input field
                if (!hasValidMember && !hasOnlySharePercentageErrors) {
                    errors.orphanStatus = 'Please provide complete information for at least one family member';
                }
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors.orphanStatus).length === 0 && 
               errors.familyMembers.every(memberError => Object.keys(memberError).length === 0);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        // Capture pre-save members for mapping pending photos (UUID -> numeric id)
        const preSaveMembers = Array.isArray(formData.familyMembers)
            ? [...formData.familyMembers]
            : [];

        const sectionData = {
            isOrphan: formData.isOrphan || false,
            familyMembers: (formData.familyMembers || []).map((member) => {
                const memberData = {
                    id: member.id,
                    name: member.name || '',
                    date_of_birth: member.dateOfBirth || '',
                    relationship: member.relationship || '',
                    gender: member.gender || '',
                    mobile_number: member.phone || '',
                    aadhaar_number: member.aadhaarNumber || '',
                    nominee: member.nominee || false,
                    share_percentage: member.sharePercentage || 0,
                    dependent: member.dependent || false,
                    user_id: employeeData.userId || employeeData.user?.id,
                    nominee_status: member.nominee || false,
                    dependent_status: member.dependent || false,
                    created_at: member.created_at,
                    updated_at: member.updated_at,
                };

                if (isESIEnabled) {
                    memberData.photo =
                        typeof member.photo === 'string' && !member.photo.startsWith('data:')
                            ? member.photo
                            : null;
                }

                return memberData;
            }),
        };

        try {
            const result = await onSaveSection(sectionData);

            // If employee is marked as having no family, skip all photo logic
            // and just persist orphan status + navigate.
            if (formData.isOrphan) {
                if (result && Object.prototype.hasOwnProperty.call(result, 'isOrphan')) {
                    setEmployeeData(prev => ({
                        ...prev,
                        isOrphan: result.isOrphan,
                        familyMembers: [],
                    }));
                }

                toast.success('Family details saved successfully', { position: 'top-right' });
                if (onNext && !isLastTab) {
                    onNext();
                }
                return { saved: true };
            }

            if (result && result.familyMembers) {
                setEmployeeData(prev => {
                    const prevMembers = Array.isArray(prev.familyMembers) ? prev.familyMembers : [];
                    const backendMembers = Array.isArray(result.familyMembers) ? result.familyMembers : [];

                    // Helper: map backend member to frontend shape
                    const toFrontend = (member, prevMatch = null) => ({
                        id: member.id || member.temp_id || prevMatch?.id || uuidv4(),
                        name: member.name || '',
                        dateOfBirth: member.date_of_birth || member.dateOfBirth || '',
                        relationship: member.relationship || '',
                        gender: member.gender || '',
                        phone: member.mobile_number || member.phone || '',
                        aadhaarNumber: member.aadhaar_number || member.aadhaarNumber || '',
                        nominee: member.nominee || member.nominee_status || false,
                        sharePercentage: member.share_percentage || member.sharePercentage || 0,
                        dependent: member.dependent || member.dependent_status || false,
                        photo: member.photo || member.photo_path || prevMatch?.photo || null,
                        photoFileName: member.photo_file_name || member.photoFileName || prevMatch?.photoFileName || '',
                        photoChanged: false,
                        user_id: member.user_id || employeeData.userId || employeeData.user?.id,
                        created_at: member.created_at,
                        updated_at: member.updated_at,
                    });

                    // Index backend members by id for stable lookup
                    const backendById = new Map();
                    backendMembers.forEach(m => {
                        if (m.id != null) backendById.set(String(m.id), m);
                    });

                    const usedBackendIds = new Set();

                    // Rebuild in previous UI order first
                    const ordered = prevMembers.map(prevM => {
                        let backendMatch = null;
                        if (prevM.id != null) {
                            backendMatch = backendById.get(String(prevM.id)) || null;
                        }

                        if (!backendMatch) {
                            backendMatch = backendMembers.find(m =>
                                m.temp_id && String(m.temp_id) === String(prevM.id)
                            ) || null;
                        }

                        if (!backendMatch) {
                            return prevM; // removed in backend; keep for this render, will disappear next load
                        }

                        usedBackendIds.add(String(backendMatch.id));
                        return toFrontend(backendMatch, prevM);
                    });

                    // Append any brand-new backend members that didn't exist in prev list
                    backendMembers.forEach(m => {
                        const key = m.id != null ? String(m.id) : null;
                        if (key && usedBackendIds.has(key)) return;
                        ordered.push(toFrontend(m, null));
                    });

                    return {
                        ...prev,
                        familyMembers: ordered,
                    };
                });
            }

            // Upload pending photos (if any)
            let anyUploadFailed = false;
            const remainingPendingById = { ...pendingFamilyPhotoFiles };
            const remainingPendingByIndex = { ...pendingFamilyPhotoFilesByIndex };

            const uploadEmployeeId = employeeData?.id || employeeData?.employeeId;

            if (uploadEmployeeId && Array.isArray(result?.familyMembers) && result.familyMembers.length > 0) {

                for (let idx = 0; idx < result.familyMembers.length; idx++) {
                    const savedMember = result.familyMembers[idx];
                    const backendKey = savedMember?.id !== undefined && savedMember?.id !== null
                        ? String(savedMember.id)
                        : null;

                    // Also look up by the pre-save UI id (may be a UUID for brand-new members)
                    const preMember = preSaveMembers[idx];
                    const preKey = preMember && preMember.id !== undefined && preMember.id !== null
                        ? String(preMember.id)
                        : null;

                    const pendingFile =
                        (backendKey && remainingPendingById[backendKey]) ||
                        (preKey && remainingPendingById[preKey]) ||
                        remainingPendingByIndex[idx] ||
                        null;
                    if (!pendingFile || !savedMember?.id) continue;

                    try {
                        const resp = await employeeService.uploadEmployeeFamilyPhoto(
                            uploadEmployeeId,
                            savedMember.id,
                            pendingFile
                        );

                        if (!resp?.success || !resp?.photoPath) {
                            anyUploadFailed = true;
                            toast.error('Photo upload failed: ' + (resp?.message || 'Unknown error'));
                            continue;
                        }

                        setEmployeeData(prev => ({
                            ...prev,
                            familyMembers: (prev.familyMembers || []).map(m => {
                                const matches =
                                    String(m.id) === String(savedMember.id) ||
                                    (savedMember.temp_id && String(m.id) === String(savedMember.temp_id));
                                if (!matches) return m;
                                return {
                                    ...m,
                                    id: savedMember.id,
                                    photo: resp.photoPath,
                                    photoFileName: pendingFile.name,
                                    photoChanged: false,
                                };
                            }),
                        }));

                        setFamilyPhotoUrlCache(prev => {
                            const next = new Map(prev);
                            next.delete(savedMember.id);
                            return next;
                        });

                        setPendingFamilyPhotoFiles(prev => {
                            const next = { ...prev };
                            if (backendKey) delete next[backendKey];
                            if (preKey) delete next[preKey];
                            return next;
                        });
                        if (backendKey) delete remainingPendingById[backendKey];
                        if (preKey) delete remainingPendingById[preKey];

                        setPendingFamilyPhotoFilesByIndex(prev => {
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                        });
                        delete remainingPendingByIndex[idx];
                    } catch (e) {
                        anyUploadFailed = true;
                        toast.error('Error uploading photo: ' + (e?.message || 'Unknown error'));
                    }
                }
            }

            if (isESIEnabled && !formData.isOrphan) {
                const hasRemainingPending =
                    Object.keys(remainingPendingById).length > 0 ||
                    Object.keys(remainingPendingByIndex).length > 0;

                // If we have any pending photos or uploads failed, or we never
                // received familyMembers back from backend, do not navigate.
                const noBackendMembers = !Array.isArray(result?.familyMembers) || result.familyMembers.length === 0;

                if (anyUploadFailed || hasRemainingPending || noBackendMembers) {
                    if (!anyUploadFailed) {
                        toast.error('Please upload required family member photos before continuing');
                    }
                    return { saved: false };
                }
            }

            if (result && Object.prototype.hasOwnProperty.call(result, 'isOrphan')) {
                setEmployeeData(prev => ({
                    ...prev,
                    isOrphan: result.isOrphan,
                }));
            }

            if (onNext && !isLastTab) {
                onNext();
            }
            toast.success('Saved successfully', { position: 'top-right' });
            return { saved: true };
        } catch (error) {
            console.error('Save failed:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });

            if (error.response?.data?.errors) {
                const backendErrors = error.response.data.errors;
                const newValidationErrors = { ...validationErrors };

                if (backendErrors.familyMembers) {
                    newValidationErrors.familyMembers = backendErrors.familyMembers.map((memberErrors) => {
                        const errors = {};
                        if (memberErrors.name) errors.name = memberErrors.name;
                        if (memberErrors.date_of_birth) errors.dateOfBirth = memberErrors.date_of_birth;
                        if (memberErrors.relationship) errors.relationship = memberErrors.relationship;
                        if (memberErrors.gender) errors.gender = memberErrors.gender;
                        if (memberErrors.mobile_number) errors.phone = memberErrors.mobile_number;
                        if (memberErrors.aadhaar_number) errors.aadhaarNumber = memberErrors.aadhaar_number;
                        if (memberErrors.photo) errors.photo = memberErrors.photo;
                        return errors;
                    });
                }

                setValidationErrors(newValidationErrors);
            }
            return { saved: false };
        }
    };

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(handleSave);
        }
    }, [onSaveTrigger, handleSave]);

    return (
        <div className="space-y-4">
            {/* Lock editing in readOnly mode, but keep preview actions (eye icon) clickable */}
            <div className={readOnly ? 'opacity-75 pointer-events-none' : ''}>
            {/* ESI Status Indicator */}
            {isESIEnabled && (
                <div className="bg-[hsl(var(--ds-accent))]/40 border border-[hsl(var(--ds-border))] rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                        <div className="text-[hsl(var(--ds-secondary))] mr-2">📋</div>
                        <div className="text-sm text-[hsl(var(--ds-secondary))]-dark">
                            <strong>ESI Enabled:</strong> Photo upload is mandatory for all family members for ESI filing.
                        </div>
                    </div>
                </div>
            )}
            
            <div>
                <label className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        name="isOrphan"
                        checked={formData.isOrphan}
                        onChange={handleInputChange}
                        className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-secondary))] shadow-sm focus:border-[hsl(var(--ds-border))] focus:ring focus:ring-[hsl(var(--ds-primary))]/25 focus:ring-opacity-50"
                    />
                    <span className="text-sm font-medium text-[hsl(var(--ds-fg))]">Having no family</span>
                </label>
            </div>
            {!formData.isOrphan && (
                <div>
                    {/* The original code had formErrors, but it's removed from the new_code.
                        Assuming the validation logic is now handled by onSaveSection or removed.
                        For now, we'll keep the structure but remove the error display. */}
                    {formData.familyMembers.map((member, index) => (
                        <div key={member.id} className="p-4 bg-[hsl(var(--ds-surface))] rounded-lg mb-4">
                            <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                                <FloatingInput
                                    id={`familyName-${index}`}
                                    label="Name"
                                    value={member.name || ''}
                                    onChange={(e) => handleNestedInputChange(index, 'name', e.target.value)}
                                    required
                                    error={validationErrors.familyMembers[index]?.name}
                                />
                                <div>
                                    <DatePicker
                                        value={member.dateOfBirth || ''}
                                        onChange={(e) => handleNestedInputChange(index, 'dateOfBirth', e.target.value)}
                                        label="Date of Birth"
                                        max={new Date().toISOString().slice(0,10)}
                                        maxErrorText="Date cannot be in the future"
                                        validateOnType={false}
                                    />
                                    {validationErrors.familyMembers[index]?.dateOfBirth && (
                                        <div className="text-red-500 text-xs mt-1">{validationErrors.familyMembers[index]?.dateOfBirth}</div>
                                    )}
                                </div>
                                <SearchableSelect
                                    id={`familyRelationship-${index}`}
                                    label="Relationship"
                                    value={member.relationship || ''}
                                    onChange={(e) => handleNestedInputChange(index, 'relationship', e.target.value)}
                                    staticOptions={[
                                        { value: 'Father', label: 'Father' },
                                        { value: 'Mother', label: 'Mother' },
                                        { value: 'Spouse', label: 'Spouse' },
                                        { value: 'Child', label: 'Child' },
                                        { value: 'Father in Law', label: 'Father in Law' },
                                        { value: 'Mother in Law', label: 'Mother in Law' },
                                        { value: 'Brother', label: 'Brother' },
                                        { value: 'Sister', label: 'Sister' },
                                    ]}
                                    required
                                    error={validationErrors.familyMembers[index]?.relationship}
                                />
                                <SearchableSelect
                                    id={`familyGender-${index}`}
                                    label="Gender"
                                    value={member.gender || ''}
                                    onChange={(e) => handleNestedInputChange(index, 'gender', e.target.value)}
                                    staticOptions={[
                                        { value: 'male', label: 'Male' },
                                        { value: 'female', label: 'Female' },
                                        { value: 'other', label: 'Other' },
                                    ]}
                                    required
                                    error={validationErrors.familyMembers[index]?.gender}
                                />
                                <FloatingInputWithNA
                                    id={`familyPhone-${index}`}
                                    label="Mobile Number"
                                    value={member.phone || ''}
                                    onChange={(e) => handleNestedInputChange(index, 'phone', e.target.value)}
                                    maxLength={10}
                                    placeholder="10-digit mobile number"
                                    required
                                    error={validationErrors.familyMembers[index]?.phone}
                                />
                                <FloatingInputWithNA
                                    id={`familyAadhaar-${index}`}
                                    label="Aadhaar No"
                                    value={member.aadhaarNumber || ''}
                                    onChange={(e) => handleNestedInputChange(index, 'aadhaarNumber', e.target.value)}
                                    maxLength={12}
                                    placeholder="12-digit Aadhaar"
                                    required
                                    error={validationErrors.familyMembers[index]?.aadhaarNumber}
                                />
                                
                                {/* Photo Upload - Always shown when ESI is enabled */}
                                {isESIEnabled && (
                                    <div className="flex flex-row items-start justify-start h-full">
                                        <div className="text-sm font-medium text-[hsl(var(--ds-fg))] mt-3 mr-2 mb-2">
                                            Photo :
                                        </div>
                                        {/* Photo Preview/Display */}
                                        {(member.photo || member.photoFileName) && (
                                            <div className="mr-3">
                                                {member.photo ? (
                                                    // Check if it's base64 or file path
                                                    typeof member.photo === 'string' && member.photo.startsWith('data:') ? (
                                                        // Base64 photo (new upload)
                                                        <div className="w-12 h-12 bg-[hsl(var(--ds-accent))]/60 rounded-lg flex items-center justify-center cursor-pointer pointer-events-auto" onClick={() => handleViewPhoto(index)}>
                                                            <img 
                                                                src={member.photo.startsWith('data:') ? member.photo : (familyPhotoUrlCache.get(member.id) || '')} 
                                                                alt="Family Photo" 
                                                                className="w-10 h-10 object-cover rounded"
                                                            />
                                                        </div>
                                                    ) : typeof member.photo === 'string' ? (
                                                        // File path (uploaded to backend)
                                                        <div className="w-12 h-12 bg-[hsl(var(--ds-accent))]/60 rounded-lg flex items-center justify-center cursor-pointer pointer-events-auto" onClick={() => handleViewPhoto(index)}>
                                                            <img 
                                                                src={member.photo.startsWith('data:') ? member.photo : (familyPhotoUrlCache.get(member.id) || '')} 
                                                                alt="Family Photo" 
                                                                className="w-10 h-10 object-cover rounded"
                                                            />
                                                        </div>
                                                    ) : (
                                                        // File object (fallback)
                                                        <div className="w-12 h-12 bg-[hsl(var(--ds-accent))]/60 rounded-lg flex items-center justify-center cursor-pointer pointer-events-auto" onClick={() => handleViewPhoto(index)}>
                                                            <img 
                                                                src={member.photo.startsWith('data:') ? member.photo : (familyPhotoUrlCache.get(member.id) || '')} 
                                                                alt="Family Photo" 
                                                                className="w-10 h-10 object-cover rounded"
                                                            />
                                                        </div>
                                                    )
                                                ) : member.photoFileName && !member.photo ? (
                                                    // Existing photo from backend
                                                    <div className="w-12 h-12 bg-[hsl(var(--ds-accent))]/60 rounded-lg flex items-center justify-center cursor-pointer pointer-events-auto" onClick={() => handleViewPhoto(index)}>
                                                        <svg className="w-6 h-6 text-[hsl(var(--ds-secondary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                        
                                        {/* Action Buttons */}
                                        <div className="flex space-x-2 mt-3">
                                            {(member.photo || member.photoFileName) && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewPhoto(index)}
                                                    className="w-6 h-6 text-[hsl(var(--ds-secondary))] bg-[hsl(var(--ds-accent))]/40 border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-accent))]/60 flex items-center justify-center pointer-events-auto"
                                                    title="View Photo"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            )}
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                                                    onChange={(e) => {
                                                        const f = e.target.files && e.target.files[0];
                                                        // Allow selecting the same file name again
                                                        e.target.value = '';
                                                        handlePhotoUpload(index, member.id, f);
                                                    }}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    id={`photo-upload-${index}`}
                                                    disabled={photoUploading[index]}
                                                />
                                                <label
                                                    htmlFor={`photo-upload-${index}`}
                                                    className={`w-6 h-6 text-purple-600 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 flex items-center justify-center cursor-pointer ${
                                                        photoUploading[index] ? 'opacity-50 cursor-not-allowed' : ''
                                                    }`}
                                                    title={member.photo || member.photoFileName ? "Replace Photo" : "Upload Photo"}
                                                >
                                                    {photoUploading[index] ? (
                                                        <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        <Upload size={16} />
                                                    )}
                                                </label>
                                            </div>
                                        </div>
                                        
                                        {validationErrors.familyMembers[index]?.photo && (
                                            <div className="text-red-500 text-xs text-center mt-1">{validationErrors.familyMembers[index].photo}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* Nominee and Dependent Section - Compact one line */}
                            <div className="mb-3 pb-3 border-b border-[hsl(var(--ds-border))]">
                                <div className="flex items-center space-x-6">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id={`familyDependent-${index}`}
                                            checked={member.dependent || false}
                                            onChange={(e) => handleNestedInputChange(index, 'dependent', e.target.checked)}
                                            className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-secondary))] shadow-sm focus:border-[hsl(var(--ds-border))] focus:ring focus:ring-[hsl(var(--ds-primary))]/25 focus:ring-opacity-50"
                                        />
                                        <label htmlFor={`familyDependent-${index}`} className="text-sm font-medium text-[hsl(var(--ds-fg))]">
                                            Dependent
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id={`familyNominee-${index}`}
                                            checked={member.nominee || false}
                                            onChange={(e) => handleNomineeChange(index, e.target.checked)}
                                            className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-secondary))] shadow-sm focus:border-[hsl(var(--ds-border))] focus:ring focus:ring-[hsl(var(--ds-primary))]/25 focus:ring-opacity-50"
                                        />
                                        <label htmlFor={`familyNominee-${index}`} className="text-sm font-medium text-[hsl(var(--ds-fg))]">
                                            Nominee
                                        </label>
                                    </div>
                                    {member.nominee && (
                                        <div className="flex items-center space-x-2">
                                            <label className="text-sm font-medium text-[hsl(var(--ds-fg))]">Share % *</label>
                                            <input
                                                type="number"
                                                value={member.sharePercentage ?? ''}
                                                onChange={(e) => handleNestedInputChange(index, 'sharePercentage', e.target.value)}
                                                placeholder="0.00"
                                                className={`w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/35 focus:border-[hsl(var(--ds-primary))] ${
                                                    validationErrors.familyMembers[index]?.sharePercentage 
                                                        ? 'border-red-500' 
                                                        : 'border-[hsl(var(--ds-border))]'
                                                }`}
                                                min="0"
                                                max="100"
                                                step="0.01"
                                            />
                                            <span className="text-sm text-[hsl(var(--ds-muted-fg))]">%</span>
                                        </div>
                                    )}
                                    {/* Share percentage error display */}
                                    {validationErrors.familyMembers[index]?.sharePercentage && (
  <div className="text-red-500 text-xs ml-6 mt-1">
    {typeof validationErrors.familyMembers[index].sharePercentage === 'string' 
      ? validationErrors.familyMembers[index].sharePercentage
      : JSON.stringify(validationErrors.familyMembers[index].sharePercentage)}
  </div>
)}

                                    {/* Total share percentage info */}
                                    {formData.familyMembers.filter(m => m.nominee).length > 1 && (
                                        <div className={`text-xs ml-6 mt-1 ${
                                            getTotalSharePercentage() > 100 ? 'text-red-600' : 
                                            getTotalSharePercentage() > 95 ? 'text-orange-600' : 'text-[hsl(var(--ds-secondary))]'
                                        }`}>
                                            Total: {getTotalSharePercentage().toFixed(2)}%
                                            {getTotalSharePercentage() > 100 && ' (Exceeds 100%)'}
                                            {getTotalSharePercentage() > 95 && getTotalSharePercentage() <= 100 && ' (Close to limit)'}
                                        </div>
                                    )}
                                    {/* Age Card */}
                                    {member.dateOfBirth && calculateAge(member.dateOfBirth) !== null && (() => {
                                        const age = calculateAge(member.dateOfBirth);
                                        return (
                                            <div className="flex items-center space-x-2 ml-6">
                                                <div className="bg-[hsl(var(--ds-accent))]/40 border border-[hsl(var(--ds-border))] rounded-lg px-2 py-1.5 flex items-center space-x-2">
                                                    <span className="text-[hsl(var(--ds-secondary))] font-semibold text-sm">
                                                        Age:
                                                    </span>
                                                    <span className="text-[hsl(var(--ds-secondary))]-dark font-bold text-base">
                                                        {age.years}
                                                    </span>
                                                    <span className="text-[hsl(var(--ds-secondary))] text-xs">
                                                        {age.years === 1 ? 'year' : 'years'}
                                                    </span>
                                                    {age.months > 0 && (
                                                        <>
                                                            <span className="text-[hsl(var(--ds-secondary))]-dark font-bold text-base">
                                                                {age.months}
                                                            </span>
                                                            <span className="text-[hsl(var(--ds-secondary))] text-xs">
                                                                {age.months === 1 ? 'month' : 'months'}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                            
                            <button
                                onClick={() => handleRemoveItem(index)}
                                className="mt-2 text-red-600 hover:text-red-800"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={handleAddItem}
                        className="m-2 text-[hsl(var(--ds-secondary))] hover:text-[hsl(var(--ds-secondary))]-dark"
                    >
                        <PlusCircle className="inline-block mr-1" size={16} /> Add Family Member
                    </button>
                </div>
            )}
            
            {/* Validation summary and orphan status error - moved above action buttons */}
            {/* Note: This error only shows for non-share-percentage validation issues */}
            {validationErrors.orphanStatus && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {validationErrors.orphanStatus}
                            </p>
                            {validationErrors.orphanStatus.includes('complete information') && (
                                <p className="text-xs text-red-600 mt-1">
                                    Please fill in all required fields for at least one family member
                                </p>
                            )}
                            {validationErrors.orphanStatus.includes('add at least one family member') && (
                                <p className="text-xs text-red-600 mt-1">
                                    Click "Add Family Member" button below to add family information
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {feedback && (
                <div className={`mt-2 ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{feedback.message}</div>
            )}

            </div>
            {/* Photo Modal */}
            {photoModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[hsl(var(--ds-surface))] rounded-lg p-6 max-w-2xl max-h-[90vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">
                                {photoModal.fileName || 'Family Photo'}
                            </h3>
                            <button
                                onClick={closePhotoModal}
                                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))]"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="flex justify-center mb-4">
                            {photoModal.photo ? (
                                <img
                                    src={photoModal.photo}
                                    alt="Family Photo"
                                    className="photo-modal-image max-w-full max-h-96 object-contain rounded-lg"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : photoModal.hasExistingPhoto ? (
                                <div className="w-48 h-48 bg-[hsl(var(--ds-accent))]/60 rounded-lg flex items-center justify-center">
                                    <svg className="w-24 h-24 text-[hsl(var(--ds-secondary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            ) : (
                                <div className="w-48 h-48 bg-[hsl(var(--ds-muted))] rounded-lg flex items-center justify-center">
                                    <svg className="w-24 h-24 text-[hsl(var(--ds-muted-fg))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-center space-x-4">
                            {photoModal.photo && (
                                <>
                                    <button
                                        onClick={downloadPhoto}
                                        className="px-4 py-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/35 focus:ring-offset-2 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Original</span>
                                    </button>
                                    <button
                                        onClick={downloadCompressedPhoto}
                                        className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 flex items-center space-x-2 rounded-md px-4 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]/50 focus-visible:ring-offset-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        <span>Compressed</span>
                                    </button>
                                </>
                            )}
                            <button
                                onClick={closePhotoModal}
                                className="px-4 py-2 bg-[hsl(var(--ds-muted))] text-white rounded-md hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}